import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import HistoryIcon from "@mui/icons-material/History";
import VersionHistoryDialog from "./VersionHistoryDialog";
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
  FormGroup,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import DeleteIcon from "@mui/icons-material/Delete";
import CodeIcon from "@mui/icons-material/Code";
import UploadIcon from "@mui/icons-material/Upload";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard, { tableHeaderCellSx } from "./components/DataCard";
import { createEmptyGenerateInputTemplateRequest } from "./models/GenerateInputTemplateRequest";
import NumericInput from "./components/NumericInput";
import apiClient from "./utils/apiClient";
import { useAuthStore } from "./store/authStore";
import { hasCedexAccess } from "./utils/roles";
import ProductDropDown from "./ProductDropDown";

const MIN_REPLICATES = 1;
const MAX_REPLICATES = 100;
const MIN_TIMEPOINT_DAY = 0;
const MAX_TIMEPOINT_DAY = 100;
const DEFAULT_TIMEPOINTS = [MIN_TIMEPOINT_DAY];

const clampValue = (value, min, max) =>
  Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));

const createCondition = (
  id,
  replicates = MIN_REPLICATES,
  timepoints = DEFAULT_TIMEPOINTS.slice()
) => ({
  id,
  name: "",
  description: "",
  replicates,
  timepoints: timepoints.slice(),
});

const DEFAULT_ANALYSIS_TYPES = [
  "amino_acid_data",
  "product_quality",
];

const ANALYSIS_TYPE_LABELS = {
  amino_acid_data: "Amino acids data",
  product_quality: "Product quality",
};

const DEFAULT_TARGET_OPTIONS = [
  "growth",
  "productivity",
  "culture_method",
];

const TARGET_OPTION_LABELS = {
  growth: "Optimize growth",
  productivity: "Optimize productivity",
  culture_method: "Optimize culture method",
};

const createDefaultAnalysisTypesState = () =>
  DEFAULT_ANALYSIS_TYPES.reduce(
    (acc, option) => ({
      ...acc,
      [option]: false,
    }),
    {}
  );

const createDefaultDesiredOutputTargetsState = () =>
  DEFAULT_TARGET_OPTIONS.reduce(
    (acc, goal) => ({
      ...acc,
      [goal]: false,
    }),
    {}
  );

const normalizeTimepoints = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return DEFAULT_TIMEPOINTS.slice();
  }
  return values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .map((value) =>
      clampValue(value, MIN_TIMEPOINT_DAY, MAX_TIMEPOINT_DAY)
    );
};

const buildConditionsFromTemplate = (template) => {
  if (!template) {
    return [createCondition(1)];
  }

  const experimentalConditions = template.experimental_conditions || {};
  const names = template.experimental_condition_names || {};
  const descriptions = template.experimental_condition_descriptions || {};
  const replicates = template.experimental_condition_replicates || {};
  const timePoints = template.time_points || {};

  const keySet = new Set([
    ...Object.keys(experimentalConditions),
    ...Object.keys(names),
    ...Object.keys(descriptions),
    ...Object.keys(replicates),
    ...Object.keys(timePoints),
  ]);

  if (keySet.size === 0) {
    return [createCondition(1)];
  }

  return Array.from(keySet).map((key, index) => {
    const conditionData = experimentalConditions[key] || {};
    const resolvedName =
      (conditionData.name ??
        names[key] ??
        key ??
        `Condition ${index + 1}`) || `Condition ${index + 1}`;
    const resolvedDescription =
      conditionData.description ?? descriptions[key] ?? "";
    const resolvedReplicates = clampValue(
      conditionData.replicates ?? replicates[key] ?? MIN_REPLICATES,
      MIN_REPLICATES,
      MAX_REPLICATES
    );
    const resolvedTimepoints = normalizeTimepoints(
      conditionData.timepoints ?? timePoints[key] ?? DEFAULT_TIMEPOINTS.slice()
    );

    return {
      ...createCondition(index + 1, resolvedReplicates, resolvedTimepoints),
      name: resolvedName,
      description: resolvedDescription,
    };
  });
};

const extractFirstArrayFromExperimentalConditions = (template, field) => {
  if (!template || !template.experimental_conditions) {
    return [];
  }

  for (const condition of Object.values(template.experimental_conditions)) {
    const candidate = condition?.[field];
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  return [];
};

const extractFirstStringFromExperimentalConditions = (template, field) => {
  if (!template || !template.experimental_conditions) {
    return undefined;
  }

  for (const condition of Object.values(template.experimental_conditions)) {
    const candidate = condition?.[field];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate;
    }
  }

  return undefined;
};

const buildAnalysisTypesState = (selectedTypes = []) => {
  const base = createDefaultAnalysisTypesState();
  (selectedTypes || []).forEach((type) => {
    if (!(type in base)) {
      base[type] = false;
    }
    base[type] = true;
  });
  return base;
};

const buildDesiredOutputTargetsState = (selectedGoals = []) => {
  const base = createDefaultDesiredOutputTargetsState();
  (selectedGoals || []).forEach((goal) => {
    if (!(goal in base)) {
      base[goal] = false;
    }
    base[goal] = true;
  });
  return base;
};

const steps = [
  "Add Experimental Conditions",
  "Configure TimePoints",
  "Select Product Offerings",
  "Desired Outputs",
  "Review & Generate",
];

const tableHeaderSx = {
  "& .MuiTableCell-root": tableHeaderCellSx,
};

const reportSectionSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  backgroundColor: "#f5f7fa",
  p: 2,
};

export default function Input() {
  const { id: routeDefinitionId } = useParams();
  const navigate = useNavigate();
  // The active definition we're editing. When the route has no :id, we'll
  // bootstrap a new draft and redirect to /input/:id once the row exists.
  const [definitionId, setDefinitionId] = useState(routeDefinitionId || null);
  const [definitionName, setDefinitionName] = useState("Untitled");
  // source_file_id is the link from a definition back to its CEDEX import
  // (or other uploaded source). The export joins raw_sheet_row on this
  // field to populate MFALite_data measurement cells (Glc/Lac/Gln/Glu/…).
  // If we drop it from the auto-save payload, the BE will preserve the
  // existing value, but echoing it explicitly here makes the linkage
  // visible end-to-end and survives any future BE refactor.
  const [sourceFileId, setSourceFileId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [conditions, setConditions] = useState([createCondition(1)]);
  const [globalReplicates, setGlobalReplicates] = useState(MIN_REPLICATES);
  // CEDEX sample -> Condition ID / Replicate No / Vi-CELL Sample ID /
  // Description mapping. Only definitions built from a CEDEX import (see
  // the sourceFileId-driven effect below) use this; everything else keeps
  // the condition-level UI/state above untouched.
  const [isCedexMappingMode, setIsCedexMappingMode] = useState(false);
  const [cedexImportId, setCedexImportId] = useState(null);
  const [sampleMapping, setSampleMapping] = useState({});
  const [sampleMappingOrder, setSampleMappingOrder] = useState([]);
  const [mappingSaveStatus, setMappingSaveStatus] = useState("idle");
  const [mappingSaveError, setMappingSaveError] = useState(null);
  const [mappingWarnings, setMappingWarnings] = useState([]);
  // Process day of the import's earliest CEDEX measurement. The server
  // numbers CEDEX days from it, so the saved value is what every
  // timepoint below is based on; the draft is the unsaved input.
  const [firstMeasurementDay, setFirstMeasurementDay] = useState(0);
  const [firstMeasurementDayDraft, setFirstMeasurementDayDraft] = useState(0);
  const [firstDaySaveStatus, setFirstDaySaveStatus] = useState("idle");
  const [firstDaySaveError, setFirstDaySaveError] = useState(null);
  const [firstDayWarnings, setFirstDayWarnings] = useState([]);
  const mappingHydratedRef = useRef(false);
  const [globalTimepoints, setGlobalTimepoints] = useState(
    DEFAULT_TIMEPOINTS.slice()
  );
  const [product, setProduct] = useState("");
  const [userId, setUserId] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [analysisTypes, setAnalysisTypes] = useState(() =>
    createDefaultAnalysisTypesState()
  );
  const [desiredOutputDescription, setDesiredOutputDescription] = useState("");
  const [desiredOutputTargets, setDesiredOutputTargets] = useState(() =>
    createDefaultDesiredOutputTargetsState()
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [downloadFilename, setDownloadFilename] = useState("");
  const [isDownloadFilenameTouched, setIsDownloadFilenameTouched] = useState(false);
  // Auto-save state. `saveStatus` is one of: idle | saving | saved | error.
  // `hydratedRef` flips true once fetchTemplate hydrates state, so the first
  // payload computation doesn't trigger an immediate save POST.
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveError, setSaveError] = useState(null);
  const hydratedRef = useRef(false);
  const user = useAuthStore((state) => state.user);
  const sessionUserId = user?.id || "";
  const effectiveUserId = userId || sessionUserId;
  const applyGlobalTimepointsToConditions = useCallback((timepoints) => {
    const safeTimepoints =
      timepoints && timepoints.length > 0
        ? timepoints.slice()
        : DEFAULT_TIMEPOINTS.slice();
    setConditions((prev) =>
      prev.map((condition) => ({
        ...condition,
        timepoints: safeTimepoints.slice(),
      }))
    );
  }, []);
  const hydrateFromTemplate = useCallback((template) => {
    if (!template) {
      return;
    }
    setUserId(template.user_id || "");
    setProduct(template.product || "");
    setSelectedProduct(template.product || "");
    // Capture the CEDEX (or other upload) linkage so the auto-save payload
    // can echo it back. Without this, every auto-save dropped the field
    // and silently severed the join the export uses to populate
    // MFALite_data measurement cells.
    setSourceFileId(template.source_file_id || null);
    const builtConditions = buildConditionsFromTemplate(template);
    const resolvedReplicates =
      builtConditions[0]?.replicates ?? MIN_REPLICATES;
    // Preserve each condition's OWN timepoints when they were derived from
    // an import (CEDEX / xlsx uploads write per-sample day lists that vary
    // condition-to-condition — e.g. Sample_stratyx_d5-1 has [4,5] while
    // Sample_stratyx_d0-1 has [0]). Previously the loader flattened every
    // condition's list to builtConditions[0]'s value, which turned every
    // per-sample day into just [0].
    //
    // The GLOBAL timepoints (used by "Apply globally" in the wizard's
    // Step 2 UX) become the UNION across all conditions, sorted ascending.
    // If every condition genuinely shares one timepoint list, the union is
    // that same list — no behavior change for the from-scratch flow.
    const unionTimepoints = Array.from(
      new Set(
        builtConditions.flatMap((c) =>
          Array.isArray(c.timepoints) ? c.timepoints.map((v) => Number(v)) : []
        )
      )
    )
      .filter((v) => Number.isFinite(v))
      .sort((a, b) => a - b);
    const resolvedGlobalTimepoints =
      unionTimepoints.length > 0
        ? unionTimepoints
        : DEFAULT_TIMEPOINTS.slice();
    const normalizedConditions = builtConditions.map((condition) => ({
      ...condition,
      // Preserve each condition's OWN replicates (already correctly
      // resolved per-condition by buildConditionsFromTemplate) instead of
      // flattening every condition to builtConditions[0]'s value. That
      // flattening was harmless before the CEDEX sample-mapping feature —
      // every condition always shared one global "Replicates per
      // condition" count by construction, so builtConditions[0]'s value
      // WAS every condition's value. It stopped being a no-op the moment
      // conditions could have genuinely different replicate counts (two
      // CEDEX samples mapped as replicate 1 and 2 of one condition, vs.
      // an untouched sample staying at 1): this line silently forced
      // every condition in the wizard's Step 5 summary (and the export
      // payload built from it) back down to whatever the first condition
      // happened to have, discarding a correct backend recompute the
      // instant it got displayed. Exact same bug class the timepoints
      // fix above already addresses — replicates just never got the
      // same fix because nothing could trigger it before now.
      replicates: condition.replicates ?? resolvedReplicates,
      timepoints:
        Array.isArray(condition.timepoints) && condition.timepoints.length > 0
          ? condition.timepoints.slice()
          : resolvedGlobalTimepoints.slice(),
    }));
    setConditions(normalizedConditions);
    setGlobalReplicates(resolvedReplicates);
    setGlobalTimepoints(resolvedGlobalTimepoints.slice());
    // Do NOT call applyGlobalTimepointsToConditions here — it would flatten
    // every condition back to the global list, defeating the fix above. The
    // user can still hit the "Apply globally" affordance in Step 2 if they
    // actually want that.
    const templateAnalysisTypes =
      (template.analysis_types && template.analysis_types.length > 0
        ? template.analysis_types
        : extractFirstArrayFromExperimentalConditions(
            template,
            "analysis_types"
          )) || [];

    const templateDesiredOutputs =
      (template.desired_outputs && template.desired_outputs.length > 0
        ? template.desired_outputs
        : extractFirstArrayFromExperimentalConditions(
            template,
            "desired_outputs"
          )) || [];

    const templateDesiredSummary =
      template.desired_output_summary ??
      extractFirstStringFromExperimentalConditions(
        template,
        "desired_output_summary"
      ) ??
      "";

    setAnalysisTypes(buildAnalysisTypesState(templateAnalysisTypes));
    setDesiredOutputTargets(
      buildDesiredOutputTargetsState(templateDesiredOutputs)
    );
    setDesiredOutputDescription(templateDesiredSummary || "");
  }, [applyGlobalTimepointsToConditions]);

  useEffect(() => {
    if (!effectiveUserId) {
      return;
    }
    let isMounted = true;
    const bootstrapDefinition = async () => {
      setIsLoadingTemplate(true);
      setLoadError(null);
      try {
        if (routeDefinitionId) {
          // Load an existing definition.
          const definition = await apiClient.get(`/input-definition/${routeDefinitionId}`);
          if (isMounted && definition) {
            setDefinitionId(definition.id || routeDefinitionId);
            setDefinitionName(definition.name || "Untitled");
            hydrateFromTemplate(definition);
          }
        } else {
          // No id in URL — create a new draft and redirect to /input/:id so
          // refreshes and shares come back to the same record.
          const created = await apiClient.post("/input-definition", {
            name: "Untitled",
            status: "draft",
          });
          if (isMounted && created?.id) {
            setDefinitionId(created.id);
            setDefinitionName(created.name || "Untitled");
            navigate(`/input/${created.id}`, { replace: true });
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Failed to load/bootstrap definition", error);
          setLoadError(
            error?.message || "Failed to load definition. Using defaults."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoadingTemplate(false);
          hydratedRef.current = true;
        }
      }
    };
    bootstrapDefinition();
    return () => {
      isMounted = false;
    };
  }, [effectiveUserId, hydrateFromTemplate, routeDefinitionId, navigate]);

  // Detect whether this definition came from a CEDEX import (vs. a blank
  // draft or an xlsx upload) by probing the existing "detail" action on
  // its source_file_id. Success -> render the sample-mapping table below
  // instead of today's condition-level Step 1 UI.
  // Without CEDEX access the API refuses these calls, so don't make them:
  // the definition then edits like any other.
  const canUseCedex = hasCedexAccess(user);
  useEffect(() => {
    if (!sourceFileId || !canUseCedex) {
      setIsCedexMappingMode(false);
      setCedexImportId(null);
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const detail = await apiClient.post("/cedex-import-request", {
          action: "detail",
          cedex_import_id: sourceFileId,
        });
        if (!isMounted) return;
        if (detail?.metadata?.status === "success" && detail.records?.[0]) {
          const savedFirstDay = Number(detail.records[0].first_measurement_day) || 0;
          setFirstMeasurementDay(savedFirstDay);
          setFirstMeasurementDayDraft(savedFirstDay);
          setCedexImportId(sourceFileId);
          setIsCedexMappingMode(true);
        } else {
          setCedexImportId(null);
          setIsCedexMappingMode(false);
        }
      } catch {
        if (isMounted) {
          setCedexImportId(null);
          setIsCedexMappingMode(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [sourceFileId, canUseCedex]);

  // Load the saved (or computed-default) sample mapping once we know this
  // is a CEDEX-sourced definition.
  useEffect(() => {
    if (!cedexImportId) {
      setSampleMapping({});
      setSampleMappingOrder([]);
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const res = await apiClient.post("/cedex-import-request", {
          action: "sample_mapping_get",
          cedex_import_id: cedexImportId,
        });
        if (!isMounted) return;
        const mapping = res?.records?.[0] || {};
        mappingHydratedRef.current = false;
        setSampleMapping(mapping);
        setSampleMappingOrder(Object.keys(mapping));
      } catch (err) {
        if (isMounted) {
          console.error("Failed to load sample mapping:", err);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [cedexImportId]);

  const handleSampleMappingFieldChange = useCallback((sampleId, field, value) => {
    setSampleMapping((prev) => ({
      ...prev,
      [sampleId]: { ...(prev[sampleId] || {}), [field]: value },
    }));
  }, []);

  // The trashcan button marks a row excluded=true (see
  // handleSampleMappingFieldChange) rather than deleting it from
  // sampleMappingOrder outright — deleting the dict entry entirely would
  // just make the backend fall back to that sample's default condition
  // instead of dropping it. These two derived lists split the full order
  // into what's shown in the table vs. what's available to restore.
  const visibleSampleMappingIds = useMemo(
    () => sampleMappingOrder.filter((id) => !sampleMapping[id]?.excluded),
    [sampleMappingOrder, sampleMapping]
  );
  const excludedSampleMappingIds = useMemo(
    () => sampleMappingOrder.filter((id) => sampleMapping[id]?.excluded),
    [sampleMappingOrder, sampleMapping]
  );

  // sampleMappingRef always mirrors the latest sampleMapping so the
  // serialized save loop below can read the CURRENT state at the moment
  // it's actually about to send, not whatever it closed over when the
  // debounce timer was scheduled.
  const sampleMappingRef = useRef(sampleMapping);
  sampleMappingRef.current = sampleMapping;

  // Guards against overlapping in-flight sample_mapping_save requests.
  // Without this, two edits made a beat apart (e.g. changing Replicate No
  // on two different rows) can each schedule their own 600ms-debounced
  // save; if the FIRST request happens to take longer to complete on the
  // server than the SECOND (materialize_cedex_to_raw_sheet_rows deletes +
  // reinserts every raw row for the import, so timing varies), its
  // response can land after the second's — and its stale
  // experimental_conditions write silently overwrites the correct, newer
  // one. Real bug hit in practice: replicate_no=2 was saved correctly in
  // cedex_import.sample_mapping, but input_definition.experimental_
  // conditions still showed replicates=1, because an older in-flight
  // save's write raced past a newer one's. Serializing (never more than
  // one request in flight, and always sending the LATEST snapshot once
  // the current one finishes) makes that ordering impossible.
  const mappingSaveInFlightRef = useRef(false);
  const mappingSavePendingRef = useRef(false);

  const runMappingSave = useCallback(async () => {
    if (mappingSaveInFlightRef.current) {
      mappingSavePendingRef.current = true;
      return;
    }
    mappingSaveInFlightRef.current = true;
    try {
      do {
        mappingSavePendingRef.current = false;
        setMappingSaveStatus("saving");
        try {
          const res = await apiClient.post("/cedex-import-request", {
            action: "sample_mapping_save",
            cedex_import_id: cedexImportId,
            mapping: sampleMappingRef.current,
          });
          setMappingSaveStatus("saved");
          setMappingSaveError(null);
          setMappingWarnings(res?.metadata?.warnings || []);
          // The save action just rebuilt this definition's
          // experimental_conditions/time_points server-side (merging any
          // newly-shared condition_ids) — refresh conditions/
          // globalTimepoints so Steps 2+ see the up-to-date grouping
          // instead of stale state from before this edit.
          if (definitionId) {
            try {
              const refreshed = await apiClient.get(`/input-definition/${definitionId}`);
              if (refreshed) {
                hydrateFromTemplate(refreshed);
              }
            } catch (refreshErr) {
              console.error("Failed to refresh definition after mapping save:", refreshErr);
            }
          }
        } catch (err) {
          console.error("Sample mapping auto-save failed:", err);
          setMappingSaveStatus("error");
          setMappingSaveError(err?.message || "Failed to save mapping");
        }
        // If another edit came in while this request was in flight, loop
        // and send one more save with the now-current snapshot instead of
        // letting a separately-scheduled request race this one.
      } while (mappingSavePendingRef.current);
    } finally {
      mappingSaveInFlightRef.current = false;
    }
  }, [cedexImportId, definitionId, hydrateFromTemplate]);

  // Auto-save the mapping ~600ms after the last edit, mirroring the
  // definition-level auto-save below. Skips the very first population
  // (right after sample_mapping_get resolves) so opening Step 1 on an
  // untouched CEDEX definition never fires a save/recompute of its own.
  useEffect(() => {
    if (!cedexImportId || sampleMappingOrder.length === 0) return;
    if (!mappingHydratedRef.current) {
      mappingHydratedRef.current = true;
      return;
    }
    const timer = setTimeout(runMappingSave, 600);
    return () => clearTimeout(timer);
  }, [sampleMapping, cedexImportId, sampleMappingOrder.length, runMappingSave]);

  // Explicit Apply rather than an autosave: the server shifts every
  // CEDEX day by the change from the stored value, so two overlapping
  // requests would shift twice. The button is disabled while one is in
  // flight.
  const handleApplyFirstMeasurementDay = useCallback(async () => {
    if (!cedexImportId) return;
    setFirstDaySaveStatus("saving");
    setFirstDaySaveError(null);
    try {
      const res = await apiClient.post("/cedex-import-request", {
        action: "first_measurement_day_save",
        cedex_import_id: cedexImportId,
        first_measurement_day: firstMeasurementDayDraft,
      });
      if (res?.metadata?.status !== "success") {
        throw new Error(res?.metadata?.message || "Failed to save first measurement day");
      }
      setFirstMeasurementDay(firstMeasurementDayDraft);
      setFirstDayWarnings(res?.metadata?.warnings || []);
      setFirstDaySaveStatus("saved");
      // The server rebuilt this definition's time_points from the
      // renumbered days — reload them.
      if (definitionId) {
        const refreshed = await apiClient.get(`/input-definition/${definitionId}`);
        if (refreshed) {
          hydrateFromTemplate(refreshed);
        }
      }
    } catch (err) {
      console.error("First measurement day save failed:", err);
      setFirstDaySaveStatus("error");
      setFirstDaySaveError(err?.message || "Failed to save first measurement day");
    }
  }, [cedexImportId, definitionId, firstMeasurementDayDraft, hydrateFromTemplate]);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAddCondition = () => {
    const newId =
      conditions.length > 0 ? Math.max(...conditions.map((c) => c.id)) + 1 : 1;
    setConditions([
      ...conditions,
      {
        ...createCondition(newId, globalReplicates, globalTimepoints),
      },
    ]);
  };

  const handleDeleteCondition = (id) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((condition) => condition.id !== id));
    }
  };

  const handleConditionChange = (id, field, value) => {
    setConditions(
      conditions.map((condition) =>
        condition.id === id ? { ...condition, [field]: value } : condition
      )
    );
  };

  const handleGlobalReplicatesChange = (value) => {
    const clamped = clampValue(value, MIN_REPLICATES, MAX_REPLICATES);
    setGlobalReplicates(clamped);
    setConditions((prev) =>
      prev.map((condition) => ({
        ...condition,
        replicates: clamped,
      }))
    );
  };

  const handleAddGlobalTimepoint = () => {
    setGlobalTimepoints((prev) => {
      const last = prev.length > 0 ? prev[prev.length - 1] : MIN_TIMEPOINT_DAY;
      const nextValue = clampValue(
        Number(last) + 1,
        MIN_TIMEPOINT_DAY,
        MAX_TIMEPOINT_DAY
      );
      const next = [...prev, nextValue];
      applyGlobalTimepointsToConditions(next);
      return next;
    });
  };

  const handleInsertGlobalTimepointAfter = (index) => {
    setGlobalTimepoints((prev) => {
      const current = Number(prev[index]);
      const neighbor = prev[index + 1];
      let insertedValue;
      if (neighbor != null) {
        // Midpoint between this row and the next, rounded to the nearest
        // whole day so the inserted entry doesn't look like noise.
        insertedValue = Math.round((current + Number(neighbor)) / 2);
        // Midpoint collapses when neighbors are adjacent (e.g. 4 → 5 →
        // round((4+5)/2)=5). Nudge by +1 so the user sees a non-duplicate
        // they can then edit.
        if (insertedValue === current) {
          insertedValue = current + 1;
        }
      } else {
        insertedValue = current + 1;
      }
      insertedValue = clampValue(insertedValue, MIN_TIMEPOINT_DAY, MAX_TIMEPOINT_DAY);
      const next = [...prev.slice(0, index + 1), insertedValue, ...prev.slice(index + 1)];
      applyGlobalTimepointsToConditions(next);
      return next;
    });
  };

  const handleDeleteGlobalTimepoint = (index) => {
    setGlobalTimepoints((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      const next = prev.filter((_, tpIndex) => tpIndex !== index);
      const safeNext = next.length > 0 ? next : DEFAULT_TIMEPOINTS.slice();
      applyGlobalTimepointsToConditions(safeNext);
      return safeNext;
    });
  };

  const handleGlobalTimepointValueChange = (index, value) => {
    setGlobalTimepoints((prev) => {
      const next = prev.map((day, tpIndex) =>
        tpIndex === index
          ? clampValue(value, MIN_TIMEPOINT_DAY, MAX_TIMEPOINT_DAY)
          : day
      );
      applyGlobalTimepointsToConditions(next);
      return next;
    });
  };

  const handleAnalysisTypeToggle = (type) => {
    setAnalysisTypes((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const handleDesiredOutputGoalToggle = (goal) => {
    setDesiredOutputTargets((prev) => ({
      ...prev,
      [goal]: !prev[goal],
    }));
  };

  const handleProductChange = (newProduct) => {
    setSelectedProduct(newProduct);
    // If the product name contains "Plus" (case insensitive), automatically check amino_acid_data
    // Otherwise, uncheck it
    const hasPlus = newProduct && newProduct.toLowerCase().includes("plus");
    setAnalysisTypes((prev) => ({
      ...prev,
      amino_acid_data: hasPlus,
    }));
  };

  const analysisTypeOptionList = useMemo(() => {
    const existingKeys = Object.keys(analysisTypes);
    const dynamic = existingKeys.filter(
      (key) => !DEFAULT_ANALYSIS_TYPES.includes(key)
    );
    return [...DEFAULT_ANALYSIS_TYPES, ...dynamic];
  }, [analysisTypes]);

  const desiredOutputGoalOptions = useMemo(() => {
    const existingKeys = Object.keys(desiredOutputTargets);
    const dynamic = existingKeys.filter(
      (key) => !DEFAULT_TARGET_OPTIONS.includes(key)
    );
    return [...DEFAULT_TARGET_OPTIONS, ...dynamic];
  }, [desiredOutputTargets]);

  const generateRequestPayload = useMemo(() => {
    const base = createEmptyGenerateInputTemplateRequest();
    const usedKeys = new Set();

    const getUniqueConditionKey = (rawName, index) => {
      const baseName = rawName || `condition_${index + 1}`;
      let candidate = baseName;
      let suffix = 2;
      while (usedKeys.has(candidate)) {
        candidate = `${baseName}_${suffix}`;
        suffix += 1;
      }
      usedKeys.add(candidate);
      return candidate;
    };

    const experimentalConditionsMap = {};
    const selectedAnalysisTypes = Object.entries(analysisTypes)
      .filter(([, include]) => include)
      .map(([type]) => type);
    const selectedDesiredGoals = Object.entries(desiredOutputTargets)
      .filter(([, include]) => include)
      .map(([goal]) => goal);

    conditions.forEach((condition, index) => {
      const normalizedName = condition.name?.trim() || undefined;
      const conditionKey = getUniqueConditionKey(
        normalizedName || `condition_${index + 1}`,
        index
      );
      const normalizedTimepoints = (condition.timepoints || [])
        .map((day) => Number(day))
        .filter((value) => Number.isFinite(value))
        .map((value) =>
          Math.min(MAX_TIMEPOINT_DAY, Math.max(MIN_TIMEPOINT_DAY, value))
        );

      experimentalConditionsMap[conditionKey] = {
        name: normalizedName || conditionKey,
        description: condition.description?.trim() || undefined,
        replicates: condition.replicates ?? MIN_REPLICATES,
        timepoints:
          normalizedTimepoints.length > 0
            ? normalizedTimepoints
            : DEFAULT_TIMEPOINTS.slice(),
      };
    });

    return {
      ...base,
      user_id: effectiveUserId || undefined,
      product: selectedProduct || product || undefined,
      // In CEDEX-mapping mode, Step 1's sample-mapping table (via the
      // sample_mapping_save action) is the SOLE owner of experimental_
      // conditions — it's recomputed server-side from the raw CEDEX data
      // + mapping, not from the FE's `conditions` array (which isn't even
      // rendered in this mode and can go stale the moment a mapping edit
      // is saved). Sending it here too would let this generic autosave —
      // which fires on ANY wizard change, e.g. picking a product — race
      // the mapping save and stomp its correct recompute with stale data.
      // Omitting it (undefined) relies on the BE's preserve-on-omission
      // fallback in _action_save to leave the existing value alone.
      experimental_conditions:
        isCedexMappingMode || Object.keys(experimentalConditionsMap).length === 0
          ? undefined
          : experimentalConditionsMap,
      analysis_types: selectedAnalysisTypes,
      desired_outputs:
        selectedDesiredGoals.length > 0 ? selectedDesiredGoals : undefined,
      desired_output_summary: desiredOutputDescription?.trim() || undefined,
      // Preserve the upload linkage so the export-side join
      // (input_definition_service.py:898) still hits raw_sheet_row and
      // measurement cells survive the round-trip. Use undefined (not
      // null) so a missing value doesn't actively unlink — the BE's
      // _action_save merge will fall back to the existing row's value.
      source_file_id: sourceFileId || undefined,
    };
  }, [
    analysisTypes,
    conditions,
    desiredOutputDescription,
    desiredOutputTargets,
    effectiveUserId,
    isCedexMappingMode,
    product,
    selectedProduct,
    sourceFileId,
  ]);

  // Auto-save the active definition ~600ms after the last edit. POSTs to
  // /input-definition-request with action="save" so the row is upserted
  // without generating the xlsx (export action handles that on download).
  useEffect(() => {
    if (!hydratedRef.current || !effectiveUserId || !definitionId) return;
    setSaveStatus("saving");
    const timer = setTimeout(async () => {
      try {
        await apiClient.post("/input-definition-request", {
          action: "save",
          data: { ...generateRequestPayload, id: definitionId, name: definitionName },
        });
        setSaveStatus("saved");
        setSaveError(null);
      } catch (err) {
        console.error("Auto-save failed:", err);
        setSaveStatus("error");
        setSaveError(err?.message || "Failed to save changes");
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [generateRequestPayload, effectiveUserId, definitionId, definitionName]);

  const getFilenameFromDisposition = (disposition) => {
    if (!disposition || typeof disposition !== "string") {
      return null;
    }

    const utf8Match = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
      } catch {
        return utf8Match[1].replace(/"/g, "");
      }
    }

    const simpleMatch = disposition.match(/filename=\"?([^\";]+)\"?/i);
    if (simpleMatch && simpleMatch[1]) {
      return simpleMatch[1];
    }

    return null;
  };

  const buildServerFilename = () => {
    const productLabel = (selectedProduct || product || "Input").trim();
    const safeProduct = productLabel
      .replace(/\s+/g, "_")
      .replace(/[^A-Za-z0-9._-]/g, "")
      .trim() || "Input";
    const now = new Date();
    const pad = (value) => String(value).padStart(2, "0");
    const timestamp = `${now.getUTCFullYear()}-${pad(
      now.getUTCMonth() + 1
    )}-${pad(now.getUTCDate())}_${pad(now.getUTCHours())}-${pad(
      now.getUTCMinutes()
    )}`;
    return `${safeProduct}_Input_Template_${timestamp}.xlsx`;
  };

  const normalizeDownloadFilename = (value) => {
    if (!value || typeof value !== "string") {
      return "";
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return "";
    }
    const sanitized = trimmed.replace(/[\\/:*?"<>|]+/g, "_");
    return sanitized.toLowerCase().endsWith(".xlsx")
      ? sanitized
      : `${sanitized}.xlsx`;
  };

  useEffect(() => {
    if (!isDownloadFilenameTouched) {
      setDownloadFilename(buildServerFilename());
    }
  }, [isDownloadFilenameTouched, product, selectedProduct]);

  const triggerDownload = (blob, filename) => {
    if (!blob) {
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Delay revocation so the browser can finish initializing the download
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      // Save first (so any unsaved tweaks are persisted) then export.
      // export returns a FileResponse from the backend; apiClient.request
      // returns {data: Blob, response} when responseType=blob.
      await apiClient.post("/input-definition-request", {
        action: "save",
        data: { ...generateRequestPayload, id: definitionId, name: definitionName },
      });
      const result = await apiClient.request(
        "POST",
        "/input-definition-request",
        { action: "export", id: definitionId },
        false,
        "blob",
        true
      );

      const blob = result?.data;
      const response = result?.response;

      if (!blob || !(blob instanceof Blob)) {
        throw new Error("Failed to generate input template file.");
      }

      const contentType =
        response?.headers?.get?.("Content-Type") || blob.type || "";
      if (contentType.includes("application/json")) {
        const text = await blob.text();
        let errorMessage = text;
        try {
          const json = JSON.parse(text);
          errorMessage =
            json?.detail || json?.message || json?.error || text || "";
        } catch {
          errorMessage = text || "";
        }
        throw new Error(
          errorMessage || "Failed to generate input template file."
        );
      }

      const requestedFilename = normalizeDownloadFilename(downloadFilename);
      const filename = requestedFilename || buildServerFilename();

      triggerDownload(blob, filename);
      setSubmitSuccess("Request submitted successfully.");
    } catch (error) {
      setSubmitError(
        error?.message || "Failed to submit request. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        if (isCedexMappingMode) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step 1: Add Experimental Conditions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Each row is one sample the CEDEX instrument reported. Give
                samples that are replicates of the same treatment the same
                Condition ID with different Replicate No — they'll be
                combined into one condition. Pair each with its Vi-CELL
                Sample ID so cell-count data lands on the right row, even if
                the two instruments numbered samples differently.
              </Typography>
              {mappingSaveError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {mappingSaveError}
                </Alert>
              )}
              {mappingWarnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {mappingWarnings.join(" ")}
                </Alert>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {mappingSaveStatus === "saving" && "Saving…"}
                  {mappingSaveStatus === "saved" && "Saved"}
                  {mappingSaveStatus === "error" && "Save failed"}
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead sx={tableHeaderSx}>
                    <TableRow>
                      <TableCell width="5%">#</TableCell>
                      <TableCell width="14%">CEDEX Sample Name</TableCell>
                      <TableCell width="13%">Vi-CELL Sample ID</TableCell>
                      <TableCell width="18%">Condition ID</TableCell>
                      <TableCell width="10%">Replicate No</TableCell>
                      <TableCell width="30%">Description</TableCell>
                      <TableCell width="10%">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleSampleMappingIds.map((sampleId, index) => {
                      const entry = sampleMapping[sampleId] || {};
                      return (
                        <TableRow key={sampleId}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{sampleId}</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="e.g., 4 or NISTCHO D0 CHECK"
                              inputProps={{ "aria-label": `Vi-CELL Sample ID for ${sampleId}` }}
                              value={entry.vicell_sample_id ?? ""}
                              onChange={(e) => {
                                // A number stays a number; anything else
                                // (e.g. "NISTCHO D0 CHECK 10-2-26") is kept
                                // as text and matched ignoring case and spaces.
                                const raw = e.target.value;
                                handleSampleMappingFieldChange(
                                  sampleId,
                                  "vicell_sample_id",
                                  raw.trim() === "" ? null : /^\d+$/.test(raw.trim()) ? Number(raw.trim()) : raw
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder={entry.shared_day0 ? "All conditions" : "e.g., Cell1 Feed A"}
                              disabled={!!entry.shared_day0}
                              value={entry.shared_day0 ? "" : entry.condition_id ?? ""}
                              onChange={(e) =>
                                handleSampleMappingFieldChange(
                                  sampleId,
                                  "condition_id",
                                  e.target.value
                                )
                              }
                            />
                            <Tooltip title="One sample measured on day 0 for the whole run: its values go into every condition's day-0 rows, and it isn't a condition of its own.">
                              <FormControlLabel
                                sx={{ mt: 0.5 }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={!!entry.shared_day0}
                                    onChange={(e) =>
                                      handleSampleMappingFieldChange(
                                        sampleId,
                                        "shared_day0",
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label={<Typography variant="caption">Day 0, all conditions</Typography>}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <NumericInput
                              size="small"
                              disabled={!!entry.shared_day0}
                              value={entry.replicate_no ?? MIN_REPLICATES}
                              min={MIN_REPLICATES}
                              max={MAX_REPLICATES}
                              onChange={(value) =>
                                handleSampleMappingFieldChange(
                                  sampleId,
                                  "replicate_no",
                                  value
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Describe the unique feature of the condition"
                              value={entry.description ?? ""}
                              onChange={(e) =>
                                handleSampleMappingFieldChange(
                                  sampleId,
                                  "description",
                                  e.target.value
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Remove this sample from the analysis">
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    handleSampleMappingFieldChange(
                                      sampleId,
                                      "excluded",
                                      true
                                    )
                                  }
                                  disabled={visibleSampleMappingIds.length === 1}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {excludedSampleMappingIds.length > 0 && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary">
                    Removed: {excludedSampleMappingIds.join(", ")}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      excludedSampleMappingIds.forEach((sampleId) =>
                        handleSampleMappingFieldChange(sampleId, "excluded", false)
                      );
                    }}
                  >
                    Restore all
                  </Button>
                </Box>
              )}
            </Box>
          );
        }
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 1: Add Experimental Conditions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Define the experimental conditions for your MFA analysis. Each condition
              should represent a distinct experimental setup or treatment.
            </Typography>

            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                gap: 2,
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2">
                  Replicates per condition
                </Typography>
                <NumericInput
                  size="small"
                  value={globalReplicates}
                  min={MIN_REPLICATES}
                  max={MAX_REPLICATES}
                  onChange={handleGlobalReplicatesChange}
                  sx={{ width: 120 }}
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddCondition}
              >
                Add Condition
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead sx={tableHeaderSx}>
                  <TableRow>
                    <TableCell width="5%">#</TableCell>
                    <TableCell width="30%">Condition Name</TableCell>
                    <TableCell width="55%">Description of Condition</TableCell>
                    <TableCell width="10%">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conditions.map((condition, index) => (
                    <TableRow key={condition.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g., Control, High Glucose"
                          value={condition.name}
                          onChange={(e) =>
                            handleConditionChange(
                              condition.id,
                              "name",
                              e.target.value
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Describe the unique feature of the condition"
                          multiline
                          minRows={3}
                          maxRows={6}
                          value={condition.description}
                          onChange={(e) =>
                            handleConditionChange(
                              condition.id,
                              "description",
                              e.target.value
                            )
                          }
                        />
                      </TableCell>
                      <TableCell width="10%">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteCondition(condition.id)}
                          disabled={conditions.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>


          </Box>
        );

      case 1:
        if (isCedexMappingMode) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step 2: Configure TimePoints
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Timepoints come from the days the CEDEX instrument measured
                each sample. Enter the process day of the first measurement
                (e.g. 3 if sampling started on D3) and every day is numbered
                from it, in the generated file and when matching Vi-CELL
                D&lt;N&gt; files.
              </Typography>
              {firstDaySaveError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {firstDaySaveError}
                </Alert>
              )}
              {firstDayWarnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {firstDayWarnings.join(" ")}
                </Alert>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <NumericInput
                  size="small"
                  label="Process day of first measurement"
                  value={firstMeasurementDayDraft}
                  min={MIN_TIMEPOINT_DAY}
                  max={MAX_TIMEPOINT_DAY}
                  onChange={(value) => {
                    setFirstMeasurementDayDraft(value);
                    setFirstDaySaveStatus("idle");
                  }}
                  sx={{ width: 260 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyFirstMeasurementDay}
                  disabled={
                    firstDaySaveStatus === "saving" ||
                    firstMeasurementDayDraft === firstMeasurementDay
                  }
                >
                  Apply
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {firstDaySaveStatus === "saving" && "Renumbering…"}
                  {firstDaySaveStatus === "saved" && "Saved"}
                  {/* Apply is disabled when nothing changed; say why, so it
                      doesn't read as a block. */}
                  {firstDaySaveStatus !== "saving" &&
                    firstDaySaveStatus !== "saved" &&
                    firstMeasurementDayDraft === firstMeasurementDay &&
                    `Days already numbered from day ${firstMeasurementDay}`}
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead sx={tableHeaderSx}>
                    <TableRow>
                      <TableCell width="15%">#</TableCell>
                      <TableCell>Timepoint (days)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {globalTimepoints.map((day, tpIndex) => (
                      <TableRow key={`cedex-${tpIndex}`}>
                        <TableCell>{tpIndex + 1}</TableCell>
                        <TableCell>{day}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        }
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 2: Configure TimePoints
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Assign timepoints to each experimental condition. Add or adjust the
              sampling days per condition as needed.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead sx={tableHeaderSx}>
                  <TableRow>
                    <TableCell width="15%">#</TableCell>
                    <TableCell width="65%">Timepoint (days)</TableCell>
                    <TableCell width="20%">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {globalTimepoints.map((day, tpIndex) => (
                    <TableRow key={`global-${tpIndex}`}>
                      <TableCell>{tpIndex + 1}</TableCell>
                      <TableCell>
                        <NumericInput
                          size="small"
                          value={day}
                          min={MIN_TIMEPOINT_DAY}
                          max={MAX_TIMEPOINT_DAY}
                          onChange={(value) =>
                            handleGlobalTimepointValueChange(tpIndex, value)
                          }
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Insert timepoint below">
                          <IconButton
                            color="primary"
                            onClick={() => handleInsertGlobalTimepointAfter(tpIndex)}
                          >
                            <VerticalAlignBottomIcon />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteGlobalTimepoint(tpIndex)}
                          disabled={globalTimepoints.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddGlobalTimepoint}
              >
                Add Timepoint
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 3: Select Product Offerings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the product offerings that apply to this experiment. Each offering determines which data tabs will be included in the input template.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <ProductDropDown
                value={selectedProduct}
                onChange={handleProductChange}
                label="Product"
              />
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead sx={tableHeaderSx}>
                  <TableRow>
                    <TableCell width="60%">Product Offering</TableCell>
                    <TableCell width="40%" align="center">
                      Include
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analysisTypeOptionList.map((type) => (
                    <TableRow key={type}>
                      <TableCell>
                        {ANALYSIS_TYPE_LABELS[type] || type}
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={analysisTypes[type]}
                          onChange={() => handleAnalysisTypeToggle(type)}
                          inputProps={{ "aria-label": `Select ${type}` }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 4: Desired Outputs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Describe the specific outcomes you want to optimize and pick the
              focus areas for the experiment.
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              value={desiredOutputDescription}
              placeholder="Provide any context or metrics you care about for the desired outputs."
              onChange={(e) => setDesiredOutputDescription(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Optimization Goals
            </Typography>
            <FormGroup>
              {desiredOutputGoalOptions.map((goal) => (
                <FormControlLabel
                  key={goal}
                  control={
                    <Checkbox
                      checked={desiredOutputTargets[goal]}
                      onChange={() => handleDesiredOutputGoalToggle(goal)}
                    />
                  }
                  label={TARGET_OPTION_LABELS[goal] || goal}
                />
              ))}
            </FormGroup>
          </Box>
        );

      case 4: {
        const selectedAnalysisTypes = Object.keys(analysisTypes).filter(
          (type) => analysisTypes[type]
        );
        const totalTimepoints = conditions.reduce(
          (sum, condition) => sum + (condition.timepoints?.length || 0),
          0
        );
        const selectedDesiredGoals = Object.keys(desiredOutputTargets).filter(
          (goal) => desiredOutputTargets[goal]
        );
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 5: Review & Generate
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review your configuration summary. When everything looks correct,
              submit the request to create the input template.
            </Typography>

            <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1">
                  Configuration Summary
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CodeIcon fontSize="small" />}
                  onClick={() => setShowJsonPreview((prev) => !prev)}
                  sx={{
                    color: "text.secondary",
                    borderColor: "divider",
                    textTransform: "none",
                  }}
                >
                  {showJsonPreview ? "Hide JSON" : "Show JSON"}
                </Button>
              </Box>

              <Box sx={{ display: "grid", gap: 2 }}>
                <Box sx={reportSectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Request Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>User ID:</strong> {effectiveUserId || "—"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Product:</strong> {selectedProduct || product || "—"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Conditions:</strong> {conditions.length}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Timepoints:</strong> {totalTimepoints}
                  </Typography>
                </Box>

                <Box sx={reportSectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Experimental Conditions & Timepoints
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="40%">Condition</TableCell>
                        <TableCell width="20%">Replicates</TableCell>
                        <TableCell width="40%">Timepoints (days)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {conditions.map((condition, index) => (
                        <TableRow key={condition.id}>
                          <TableCell>
                            {condition.name?.trim() || `Condition ${index + 1}`}
                          </TableCell>
                          <TableCell>{condition.replicates}</TableCell>
                          <TableCell>
                            {(condition.timepoints || []).length > 0
                              ? condition.timepoints.join(", ")
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                <Box sx={reportSectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Desired Outputs
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {desiredOutputDescription?.trim()
                      ? desiredOutputDescription.trim()
                      : "No additional details provided."}
                  </Typography>
                  {selectedDesiredGoals.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {selectedDesiredGoals.map((goal) => (
                        <li key={goal}>
                          <Typography variant="body2">
                            {TARGET_OPTION_LABELS[goal] || goal}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No optimization goals selected.
                    </Typography>
                  )}
                </Box>

                <Box sx={reportSectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Product Offerings
                  </Typography>
                  {selectedAnalysisTypes.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {selectedAnalysisTypes.map((type) => (
                        <li key={type}>
                          <Typography variant="body2">
                            {ANALYSIS_TYPE_LABELS[type] || type}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No product offerings selected.
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ ...reportSectionSx, mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Download Filename (optional)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={downloadFilename}
                  onChange={(e) => {
                    setDownloadFilename(e.target.value);
                    setIsDownloadFilenameTouched(true);
                  }}
                  placeholder="Leave blank to use the server-provided filename"
                  helperText="If provided, this overrides the server filename for the download."
                />
              </Box>

              {showJsonPreview && (
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: "#0b2431",
                    color: "#e0f7fa",
                    p: 2,
                    borderRadius: 1,
                    maxHeight: 320,
                    overflow: "auto",
                    mt: 2,
                    fontSize: 13,
                  }}
                >
                  {JSON.stringify(generateRequestPayload, null, 2)}
                </Box>
              )}
            </Paper>

            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}
            {submitSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {submitSuccess}
              </Alert>
            )}
          </Box>
        );
      }

      default:
        return null;
    }
  };

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<UploadIcon />}
          title="Generate Input File"
          subtitle={
            definitionId
              ? `Editing definition · ${definitionName}`
              : "Create a new input file for Metabolic Flux Analysis"
          }
        />
        <DataCard>
            {loadError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {loadError}
              </Alert>
            )}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
              <TextField
                label="Definition name"
                value={definitionName}
                onChange={(e) => setDefinitionName(e.target.value)}
                size="small"
                sx={{ flex: 1, maxWidth: 480 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<HistoryIcon />}
                disabled={!definitionId}
                onClick={() => setHistoryOpen(true)}
              >
                History
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => navigate("/input-library")}
              >
                ← Back to Library
              </Button>
            </Box>
            <VersionHistoryDialog
              open={historyOpen}
              definitionId={definitionId}
              definitionName={definitionName}
              onClose={() => setHistoryOpen(false)}
              onRestored={() => window.location.reload()}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1, minHeight: 20 }}>
              {saveStatus === "saving" && (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Saving…
                </Typography>
              )}
              {saveStatus === "saved" && (
                <Typography variant="caption" sx={{ color: "success.main" }}>
                  All changes saved
                </Typography>
              )}
              {saveStatus === "error" && (
                <Typography variant="caption" sx={{ color: "error.main" }}>
                  Auto-save failed{saveError ? `: ${saveError}` : ""}
                </Typography>
              )}
            </Box>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {renderStepContent(activeStep)}

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isSubmitting || isLoadingTemplate}
              >
                {activeStep === steps.length - 1
                  ? isSubmitting
                    ? "Generating..."
                    : "Generate and Download Input File"
                  : "Next"}
              </Button>
            </Box>
        </DataCard>
      </PageContainer>
    </PageShell>
  );
}
